// Advanced Response-Based Scoring System
import { ASSESSMENT_CATEGORIES } from './categories';

export const calculateAdvancedScores = (categoryId, answers) => {
  switch (categoryId) {
    case 'personality':
      return calculatePersonalityScore(answers);
    case 'cognitive':
      return calculateCognitiveScore(answers);
    case 'emotional_intelligence':
      return calculateEIScore(answers);
    case 'situational_judgement':
      return calculateSJTScore(answers);
    case 'motivation_values':
      return calculateMotivationScore(answers);
    case 'behavioral':
      return calculateBehavioralScore(answers);
    case 'mindfulness':
      return calculateMindfulnessScore(answers);
    default:
      return { score: 75, insights: ['Assessment completed'] };
  }
};

// 1. Personality Scoring (Big Five Traits)
const calculatePersonalityScore = (answers) => {
  const traits = {
    openness: { total: 0, count: 0 },
    conscientiousness: { total: 0, count: 0 },
    extraversion: { total: 0, count: 0 },
    agreeableness: { total: 0, count: 0 },
    neuroticism: { total: 0, count: 0 }
  };
  
  // Process each answer based on question type and trait mapping
  Object.keys(answers).forEach(questionId => {
    const answer = answers[questionId];
    const questionNum = parseInt(questionId.replace('p', ''));
    
    // Likert questions (1-25)
    if (questionNum <= 25) {
      const traitMapping = getTraitMapping(questionNum);
      if (traitMapping.trait && traits[traitMapping.trait]) {
        const value = traitMapping.reverse ? (6 - answer) : answer;
        traits[traitMapping.trait].total += value;
        traits[traitMapping.trait].count += 1;
      }
    }
    // Situational questions (26-35) - weighted scoring
    else if (questionNum <= 35) {
      const weights = getSituationalWeights(questionNum, answer);
      Object.keys(weights).forEach(trait => {
        if (traits[trait]) {
          traits[trait].total += weights[trait];
          traits[trait].count += 1;
        }
      });
    }
    // Forced choice questions (36-45)
    else if (questionNum <= 45) {
      const weights = getForcedChoiceWeights(questionNum, answer);
      Object.keys(weights).forEach(trait => {
        if (traits[trait]) {
          traits[trait].total += weights[trait];
          traits[trait].count += 1;
        }
      });
    }
  });
  
  // Calculate trait percentiles
  const traitScores = {};
  Object.keys(traits).forEach(trait => {
    if (traits[trait].count > 0) {
      traitScores[trait] = Math.min(5, Math.max(1, traits[trait].total / traits[trait].count));
    } else {
      traitScores[trait] = 3;
    }
  });
  
  // Weighted overall score
  const overallScore = Math.round(
    (traitScores.openness * 0.2 + 
     traitScores.conscientiousness * 0.25 + 
     traitScores.extraversion * 0.2 + 
     traitScores.agreeableness * 0.2 + 
     (6 - traitScores.neuroticism) * 0.15) * 20
  );
  
  return {
    score: overallScore,
    profile: traitScores,
    insights: generatePersonalityInsights(traitScores)
  };
};

// 2. Cognitive Ability Scoring (50 Questions - IRT-based)
const calculateCognitiveScore = (answers) => {
  const categories = {
    numerical: { correct: 0, total: 0, weight: 0.3 },
    logical: { correct: 0, total: 0, weight: 0.3 },
    verbal: { correct: 0, total: 0, weight: 0.2 },
    analytical: { correct: 0, total: 0, weight: 0.2 }
  };
  
  // Get cognitive questions from categories
  const cognitiveQuestions = ASSESSMENT_CATEGORIES.find(cat => cat.id === 'cognitive')?.questions || [];
  
  let totalScore = 0;
  let totalWeight = 0;
  
  cognitiveQuestions.forEach(question => {
    const answer = answers[question.id];
    if (answer !== undefined) {
      const isCorrect = answer === question.correct;
      const difficulty = question.difficulty || 0.5;
      const category = question.category;
      
      if (categories[category]) {
        categories[category].total += 1;
        if (isCorrect) {
          categories[category].correct += 1;
        }
        
        // IRT-based scoring with difficulty weighting
        const itemScore = isCorrect ? 
          (1 + (1 - difficulty)) * 100 : 
          difficulty * 20; // Partial credit for incorrect answers
        
        totalScore += itemScore * categories[category].weight;
        totalWeight += categories[category].weight;
      }
    }
  });
  
  // Calculate category-wise performance
  const categoryScores = {};
  Object.keys(categories).forEach(cat => {
    if (categories[cat].total > 0) {
      categoryScores[cat] = Math.round((categories[cat].correct / categories[cat].total) * 100);
    }
  });
  
  const overallScore = totalWeight > 0 ? Math.round(totalScore / totalWeight) : 50;
  
  return {
    score: Math.max(0, Math.min(100, overallScore)),
    categoryScores,
    insights: generateCognitiveInsights(overallScore, categoryScores)
  };
};

// 3. Emotional Intelligence Scoring (Multi-dimensional)
const calculateEIScore = (answers) => {
  const dimensions = {
    selfAwareness: { total: 0, count: 0 },
    selfRegulation: { total: 0, count: 0 },
    socialAwareness: { total: 0, count: 0 }
  };
  
  // Map questions to EI dimensions
  const mapping = {
    'ei1': { dimension: 'socialAwareness', weight: 1.0 },
    'ei2': { dimension: 'selfRegulation', weight: 1.2 },
    'ei3': { dimension: 'socialAwareness', weight: 1.1 }
  };
  
  Object.keys(answers).forEach(qId => {
    if (mapping[qId]) {
      const { dimension, weight } = mapping[qId];
      dimensions[dimension].total += answers[qId] * weight;
      dimensions[dimension].count += weight;
    }
  });
  
  const dimensionScores = {};
  Object.keys(dimensions).forEach(dim => {
    dimensionScores[dim] = dimensions[dim].count > 0 ? 
      dimensions[dim].total / dimensions[dim].count : 3;
  });
  
  const overallScore = Math.round(
    Object.values(dimensionScores).reduce((sum, score) => sum + score, 0) / 
    Object.keys(dimensionScores).length * 20
  );
  
  return {
    score: overallScore,
    profile: dimensionScores,
    insights: generateEIInsights(dimensionScores)
  };
};

// Helper Functions
const getTraitMapping = (questionNum) => {
  const mappings = {
    1: { trait: 'neuroticism', reverse: true },
    2: { trait: 'openness', reverse: false },
    3: { trait: 'conscientiousness', reverse: false },
    4: { trait: 'neuroticism', reverse: true },
    5: { trait: 'conscientiousness', reverse: false },
    // ... continue for all 25 questions
  };
  return mappings[questionNum] || { trait: 'openness', reverse: false };
};

const getSituationalWeights = (questionNum, answer) => {
  // Return trait weights based on situational response
  const situationalWeights = {
    26: { // Teammate low-quality work
      0: { agreeableness: 2, conscientiousness: 3 },
      1: { agreeableness: 5, conscientiousness: 4 },
      2: { agreeableness: 1, conscientiousness: 4 },
      3: { agreeableness: 3, conscientiousness: 5 }
    }
    // ... continue for questions 26-35
  };
  
  return situationalWeights[questionNum]?.[answer] || {};
};

const getForcedChoiceWeights = (questionNum, answer) => {
  // Return trait weights for forced-choice questions (36-45)
  const choiceWeights = {
    37: {
      0: { openness: 5, conscientiousness: 2 },
      1: { openness: 2, conscientiousness: 5 }
    },
    38: {
      0: { conscientiousness: 5, openness: 2 },
      1: { conscientiousness: 2, openness: 4 }
    },
    39: {
      0: { extraversion: 5 },
      1: { extraversion: 2 }
    },
    40: {
      0: { openness: 5, neuroticism: 2 },
      1: { openness: 2, neuroticism: 4 }
    },
    41: {
      0: { openness: 5 },
      1: { openness: 3 }
    },
    42: {
      0: { extraversion: 5, agreeableness: 3 },
      1: { extraversion: 3, agreeableness: 5 }
    },
    43: {
      0: { extraversion: 4, conscientiousness: 2 },
      1: { extraversion: 2, conscientiousness: 5 }
    },
    44: {
      0: { neuroticism: 1 },
      1: { neuroticism: 4 }
    },
    45: {
      0: { extraversion: 2 },
      1: { extraversion: 5 }
    }
  };
  
  return choiceWeights[questionNum]?.[answer] || {};
};

const generatePersonalityInsights = (traits) => {
  const insights = [];
  
  if (traits.openness >= 4) {
    insights.push('High creativity and adaptability - excellent for innovation roles');
  }
  if (traits.conscientiousness >= 4) {
    insights.push('Strong organizational skills and reliability - natural project manager');
  }
  if (traits.extraversion >= 4) {
    insights.push('Natural leadership and communication abilities');
  }
  if (traits.agreeableness >= 4) {
    insights.push('Excellent team collaboration and conflict resolution skills');
  }
  if (traits.neuroticism <= 2) {
    insights.push('Exceptional emotional stability under pressure');
  }
  
  return insights;
};

const generateCognitiveInsights = (overallScore, categoryScores) => {
  const insights = [];
  
  // Overall performance insights
  if (overallScore >= 85) {
    insights.push('Exceptional cognitive abilities across all reasoning domains');
  } else if (overallScore >= 70) {
    insights.push('Strong analytical and problem-solving capabilities');
  } else if (overallScore >= 50) {
    insights.push('Adequate cognitive performance with development opportunities');
  } else {
    insights.push('Focus on strengthening core reasoning skills');
  }
  
  // Category-specific insights
  if (categoryScores.numerical >= 80) {
    insights.push('Excellent numerical reasoning - ideal for data-driven roles');
  } else if (categoryScores.numerical <= 40) {
    insights.push('Consider developing quantitative analysis skills');
  }
  
  if (categoryScores.logical >= 80) {
    insights.push('Strong logical reasoning - excellent for complex problem-solving');
  } else if (categoryScores.logical <= 40) {
    insights.push('Practice logical thinking and deductive reasoning');
  }
  
  if (categoryScores.verbal >= 80) {
    insights.push('Superior verbal reasoning - strong communication potential');
  } else if (categoryScores.verbal <= 40) {
    insights.push('Focus on improving language comprehension and expression');
  }
  
  if (categoryScores.analytical >= 80) {
    insights.push('Exceptional pattern recognition and analytical thinking');
  } else if (categoryScores.analytical <= 40) {
    insights.push('Develop pattern recognition and systematic analysis skills');
  }
  
  return insights;
};

const generateEIInsights = (dimensions) => {
  const insights = [];
  
  if (dimensions.selfAwareness >= 4) {
    insights.push('High self-awareness enables effective self-management');
  }
  if (dimensions.selfRegulation >= 4) {
    insights.push('Excellent emotional control and stress management');
  }
  if (dimensions.socialAwareness >= 4) {
    insights.push('Strong ability to read and respond to others\' emotions');
  }
  
  return insights;
};

export default calculateAdvancedScores;